import * as React from "react";
import gsap from "gsap";

const Section = ({
  id,
  title,
  titleDirection,
  className,
  children,
}: {
  id: string;
  title: string;
  titleDirection: SectionTitleDirection;
  className?: string;
  children: any;
}) => {
  const comp = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReducedMotion) {
      gsap.set(comp.current, { opacity: 1 });
      return;
    }

    let context = gsap.context(() => {
      gsap.set(comp.current, { opacity: 0 });
      gsap.to(comp.current, {
        delay: 0.2,
        opacity: 1,
        duration: 0.4,
        ease: "power3.inOut",
        scrollTrigger: {
          trigger: comp.current,
          // markers: true,
          start: "start 100%",
        },
      });
    }, [comp.current]);
    return () => context.revert();
  }, []);

  return (
    <div
      className={`relative mb-[16vh] flex h-fit min-h-[55vh] w-full scroll-mt-[90px] flex-col pt-[5vh] md:min-h-[60vh] ${
        titleDirection === SectionTitleDirection.Left
          ? "bg-surface/[0.12]"
          : "bg-surface/[0.06]"
      } ${className}`}
      ref={comp}
      id={id}
    >
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-main/70 to-transparent" />
      <div className="mx-auto flex h-fit w-full max-w-7xl flex-col space-y-5 px-4 pt-2 pb-6 xl:px-1">
        <SectionTitle title={title} direction={titleDirection} />
        <div className="sectionBody flex h-fit w-full snap-center flex-col flex-wrap content-center items-center space-y-4">
          {children}
        </div>
      </div>
    </div>
  );
};

enum SectionTitleDirection {
  Left,
  Right,
}

const SectionTitle = ({
  title,
  direction,
}: {
  title: string;
  direction: SectionTitleDirection;
}) => {
  if (direction == SectionTitleDirection.Right) {
    return (
      <div className="contentBox flex h-fit w-full flex-row content-end items-center justify-end space-x-4 text-right align-middle md:items-end lg:mb-6 lg:mt-12">
        <div className="h-[2px] max-h-[2px] min-h-[2px] w-full bg-gradient-to-r from-tertiary/5 via-tertiary/90 to-tertiary/5 md:mb-2" />
        <h1 className="w-fit whitespace-nowrap">{title}</h1>
      </div>
    );
  } else {
    return (
      <div className="contentBox flex h-fit w-full flex-row content-start items-center justify-start space-x-4 text-left align-middle md:items-end lg:mb-6 lg:mt-12">
        <h1 className="w-fit whitespace-nowrap">{title}</h1>
        <div className="h-[2px] max-h-[2px] min-h-[2px] w-full bg-gradient-to-r from-tertiary/5 via-tertiary/90 to-tertiary/5 md:mb-2" />
      </div>
    );
  }
};

export { Section, SectionTitle, SectionTitleDirection };
